import React from 'react';
import { Link } from 'react-router-dom';
import Seo, { SITE } from '../../components/Seo/Seo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faCalendarDays,
	faCalendarCheck,
	faClock,
	faCircleCheck,
	faCreditCard,
	faStar,
	faPhone,
	faEnvelope,
	faDownload,
	faRightToBracket,
	faBell,
	faMusic,
	faLaptopMobile,
	faArrowRight,
} from '@fortawesome/pro-regular-svg-icons';

import {
	SEASON_LABEL,
	RECITALS,
	TUITION_SEASON_TOTAL_LABEL,
	TUITION_MONTHLY_LABEL,
	TUITION_INSTALLMENTS,
	TUITION_SCHEDULE_LABEL,
	formatSeasonDate,
	firstLessonCohorts,
} from '../../data/seasonData';
import { googleCalendarUrl, buildIcs, downloadIcs } from '../../utils/calendarLinks';
import SeasonMonthGrid from '../CustomerPortal/SeasonMonthGrid';

import './Fall.css';

// The public season-information page — the single destination the returning-family
// welcome email links to.
//
// WHY PUBLIC, and it is the whole design: families cannot reach the portal until
// they claim their account, so the portal cannot be the answer to "how do I use
// the portal." Public also means this survives the email being deleted, works on
// a phone, forwards to a second parent, and can be pasted as a reply to the same
// question for the rest of the season. That reusability is the return on it.
//
// EVERY date and dollar figure on this page reads from season canon
// (src/canon/season.json via ../../data/seasonData). Nothing here is typed by
// hand — not the tuition, not the recital dates, not the first-lesson days. The
// tuition figure used to live hardcoded in five places across three files, which
// is how a site comes to quote two prices on two pages. Do not be the regression.

// The walkthrough screen recording. Set to a URL and the section appears; empty
// and the section renders NOTHING — the page was deliberately shipped without it
// rather than blocked on it (2026-08-07 → 2026-08-21).
//
// This must be an EMBED url, not a share link: it feeds an <iframe src>, and
// youtube.com/watch?v=... or youtu.be/... will not render in a frame.
//
// youtube-NOCOOKIE deliberately, not youtube.com: it sets no tracking cookies
// until the viewer presses play, which is the right default on a family-facing
// page that also routes parents to a form collecting bank details.
//
// THE RECORDING IS PORTRAIT — 550 x 1192, 4:57. `.fl-video` in Fall.css is sized
// for that exact ratio and capped in width; re-pointing this at a landscape
// video means changing that block too.
const WALKTHROUGH_VIDEO_URL = 'https://www.youtube-nocookie.com/embed/2HMJS-uPRhY';

// The day My Music Staff is switched off for good.
//
// This is deliberately NOT in src/canon/season.json and should not be moved
// there: season canon holds facts that RECUR every season (first-lesson days,
// closures, recitals, the year's tuition), and this is a one-time migration
// event that happens exactly once and is then historical. Putting it in canon
// would leave a dead field every future season has to ignore. It gets a named
// constant here instead so it has one findable home on the one page that states
// it, rather than being buried inline in the copy.
const MMS_RETIREMENT_LABEL = 'August 31, 2026';

// A recital rendered as an all-day calendar event. Season events carry a date
// only (see src/canon/season.json), so calendarLinks emits them all-day.
const recitalEvent = (recital) => ({
	title: `${recital.name} — Color Your Voice`,
	date: recital.date,
	details: 'Color Your Voice recital. No regular lessons this day.',
	location: recital.location || '',
});

const Fall = () => {
	const cohorts = firstLessonCohorts();

	return (
		<main className='cyv fall-page'>
			<Seo
				title={`${SEASON_LABEL} Season Information | Color Your Voice`}
				description={`Everything returning Color Your Voice families need for the ${SEASON_LABEL} school year — when lessons start, the new studio portal, tuition, recital dates, the season calendar, and how make-ups work.`}
				canonical={`${SITE}/fall`}
			/>

			{/* ---- Hero ---- */}
			<header className='pg-hero'>
				<span className='eyebrow'>{SEASON_LABEL} school year</span>
				<h1 className='brand-heading'>Welcome back</h1>
				<p>
					Everything you need for the year ahead, in one place — when lessons
					start, your new studio portal, tuition, recital dates and how make-ups
					work. <strong>Keep this link;</strong> it stays here all season; so
					there is nothing to dig out of an old email.
				</p>
			</header>

			{/* ---- 1. When lessons start ---- */}
			<section className='fl' aria-labelledby='fl-start'>
				<div className='fl-sec__head'>
					<span className='eyebrow'>First up</span>
					<h2 id='fl-start' className='brand-heading'>
						When lessons start
					</h2>
					<p>
						Your child's first lesson is the first time their weekday comes
						around. Everyone keeps the same weekly time from there.
					</p>
				</div>

				<div className='cyv-card fl-card' style={{ '--accent': 'var(--grad-lessons)' }}>
					<span className='accent-bar' />
					<ul className='fl-startlist'>
						{cohorts.map((c) => (
							<li key={c.key} className='fl-startlist__row'>
								<span className='fl-startlist__dow'>
									<FontAwesomeIcon icon={faCalendarDays} className='fl-i fl-i--orange' />
									{c.weekday}
								</span>
								<span className='fl-startlist__date'>{c.label}</span>
							</li>
						))}
					</ul>
					<p className='fl-card__fine'>
						<FontAwesomeIcon icon={faClock} className='fl-i fl-i--teal' />
						One 45-minute lesson each week, at your standing time.
					</p>
				</div>
			</section>

			{/* ---- 2. Your new studio portal — the section the email points at ---- */}
			<section className='fl' aria-labelledby='fl-portal'>
				<div className='fl-sec__head'>
					<span className='eyebrow'>New this year</span>
					<h2 id='fl-portal' className='brand-heading'>
						Your new studio portal
					</h2>
					<p>
						Anthony built it for us over the summer, and it lives right here on
						our own website — replacing My Music Staff, the third-party
						scheduler we have used until now. Your schedule, cancellations,
						make-ups, lesson notes and payments all live in one place now — on
						your phone, with nothing to install.
					</p>
				</div>

				<div className='fl-grid'>
					<article className='cyv-card fl-card' style={{ '--accent': 'var(--grad-brand)' }}>
						<span className='accent-bar' />
						<h3>
							<FontAwesomeIcon icon={faLaptopMobile} className='fl-i fl-i--teal' />
							What lives there
						</h3>
						<ul className='fl-list'>
							<li>
								<FontAwesomeIcon icon={faCalendarCheck} />
								<span>
									<strong>Your schedule</strong> — every upcoming lesson, with
									closures and recital days already marked.
								</span>
							</li>
							<li>
								<FontAwesomeIcon icon={faCircleCheck} />
								<span>
									<strong>Cancellations and make-ups</strong> — cancel a lesson
									yourself, and book your own make-up from the open times we
									publish.
								</span>
							</li>
							<li>
								<FontAwesomeIcon icon={faMusic} />
								<span>
									<strong>Lesson notes</strong> — what your child is working on,
									song by song, written by whichever teaching artist they saw.
								</span>
							</li>
							<li>
								<FontAwesomeIcon icon={faBell} />
								<span>
									<strong>Announcements</strong> — studio news, so it is not
									only in your inbox.
								</span>
							</li>
							<li>
								<FontAwesomeIcon icon={faCreditCard} />
								<span>
									<strong>Payments</strong> — your payment method and your
									billing history.
								</span>
							</li>
						</ul>
					</article>

					<article className='cyv-card fl-card' style={{ '--accent': 'var(--grad-lessons)' }}>
						<span className='accent-bar' />
						<h3>
							<FontAwesomeIcon icon={faRightToBracket} className='fl-i fl-i--orange' />
							How you get in
						</h3>
						<ol className='fl-steps'>
							<li>
								<span className='fl-steps__n'>1</span>
								<span>
									<strong>Watch for an email from us.</strong> It carries a
									set-up link that is specific to your family — it is not a
									password, and it is not shared.
								</span>
							</li>
							<li>
								<span className='fl-steps__n'>2</span>
								<span>
									<strong>Choose your own password</strong> and confirm who is
									in your family. That is the whole set-up.
								</span>
							</li>
							<li>
								<span className='fl-steps__n'>3</span>
								<span>
									<strong>Add it to your phone's home screen</strong> if you
									like — it then opens like an app.
								</span>
							</li>
						</ol>
						<p className='fl-card__fine'>
							My Music Staff closes <strong>{MMS_RETIREMENT_LABEL}</strong>.
							After that, the studio portal is the only place your schedule
							lives — so please set yours up when the email arrives.
						</p>
						<div className='fl-card__cta'>
							<Link className='btn-cyv-ghost' to='/portal/login'>
								Already set up? Sign in
								<FontAwesomeIcon icon={faArrowRight} className='fl-i fl-i--after' />
							</Link>
						</div>
					</article>
				</div>
			</section>

			{/* ---- 3. Walkthrough video — renders nothing until one exists ---- */}
			{WALKTHROUGH_VIDEO_URL && (
				<section className='fl' aria-labelledby='fl-video'>
					<div className='fl-sec__head'>
						{/* "Two minutes" / "A quick walkthrough" until 2026-08-21, written
						    when the recording was hypothetical. It runs 4:57, so both
						    overstated it — and the returning-family welcome email was
						    edited the same day to say "a walkthrough of the portal"
						    rather than "a short walkthrough". The page and the email
						    have to agree: a parent who is told "quick" and given five
						    minutes stops watching. */}
						<span className='eyebrow'>Five minutes</span>
						<h2 id='fl-video' className='brand-heading'>
							A walkthrough of the portal
						</h2>
						<p>
							Anthony walks through finding your lesson time, cancelling a
							lesson and booking a make-up.
						</p>
					</div>
					<div className='fl-video'>
						<iframe
							src={WALKTHROUGH_VIDEO_URL}
							title='Color Your Voice family portal walkthrough'
							loading='lazy'
							referrerPolicy='strict-origin-when-cross-origin'
							allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
							allowFullScreen
						/>
					</div>
				</section>
			)}

			{/* ---- 4. Tuition ---- */}
			<section className='fl' aria-labelledby='fl-tuition'>
				<div className='fl-sec__head'>
					<span className='eyebrow'>What it costs</span>
					<h2 id='fl-tuition' className='brand-heading'>
						Tuition
					</h2>
				</div>

				<div className='cyv-card fl-card' style={{ '--accent': 'var(--grad-lessons)' }}>
					<span className='accent-bar' />
					<div className='fl-price'>
						<span className='fl-price__n'>{TUITION_SEASON_TOTAL_LABEL}</span>
						<span className='fl-price__u'>per student, for the school year · tax included</span>
					</div>
					<p className='fl-price__note'>
						School-year tuition, split across {TUITION_INSTALLMENTS} months —{' '}
						{TUITION_INSTALLMENTS} payments of {TUITION_MONTHLY_LABEL} on the
						1st, {TUITION_SCHEDULE_LABEL}.
					</p>
					<ul className='fl-list'>
						<li>
							<FontAwesomeIcon icon={faCircleCheck} />
							<span>
								Tuition is <strong>per student</strong> — a family with two
								children enrolled pays two tuitions.
							</span>
						</li>
						<li>
							<FontAwesomeIcon icon={faCircleCheck} />
							<span>
								June's payment is the last installment of the year. It closes
								out the total rather than paying for June lessons, which do not
								run.
							</span>
						</li>
						<li>
							<FontAwesomeIcon icon={faCircleCheck} />
							<span>
								Summer programming is optional and billed separately from
								school-year tuition.
							</span>
						</li>
					</ul>
					<p className='fl-card__fine'>
						Full billing terms, including paying in full by check, are on{' '}
						<Link to='/policies#tuition'>our policies page</Link>.
					</p>
				</div>
			</section>

			{/* ---- 5. Recital dates ---- */}
			<section className='fl' aria-labelledby='fl-recitals'>
				<div className='fl-sec__head'>
					<span className='eyebrow'>Save the date</span>
					<h2 id='fl-recitals' className='brand-heading'>
						Recitals
					</h2>
					<p>
						Two recitals this season. Performance times are announced closer to
						each date — add them now so the day itself is already blocked out.
					</p>
				</div>

				<div className='fl-grid'>
					{RECITALS.map((r) => (
						<article
							key={r.name}
							className='cyv-card fl-card fl-card--recital'
							style={{ '--accent': 'var(--grad-brand)' }}>
							<span className='accent-bar' />
							<h3>
								<FontAwesomeIcon icon={faStar} className='fl-i fl-i--orange' />
								{r.name}
							</h3>
							<p className='fl-recital__date'>{formatSeasonDate(r.date)}</p>
							{r.location && <p className='fl-recital__loc'>{r.location}</p>}
							<div className='fl-card__cta fl-card__cta--row'>
								<a
									className='btn-cyv-ghost'
									href={googleCalendarUrl(recitalEvent(r))}
									target='_blank'
									rel='noopener noreferrer'>
									<FontAwesomeIcon icon={faCalendarDays} className='fl-i fl-i--teal' />
									Google Calendar
								</a>
								<button
									type='button'
									className='btn-cyv-ghost'
									onClick={() =>
										downloadIcs(
											`cyv-${r.name.toLowerCase().replace(/\s+/g, '-')}`,
											buildIcs(recitalEvent(r))
										)
									}>
									<FontAwesomeIcon icon={faDownload} className='fl-i fl-i--teal' />
									Apple / Outlook
								</button>
							</div>
						</article>
					))}
				</div>
			</section>

			{/* ---- 6. Season calendar ----
			    showFlex stays FALSE here: to a family a flex day simply IS a day the
			    studio is closed, which is true. Families are never shown the word
			    "flex" — see FLEX_PUBLIC_NAME in seasonData. */}
			<section className='fl' aria-labelledby='fl-calendar'>
				<div className='fl-sec__head'>
					<span className='eyebrow'>The whole year</span>
					<h2 id='fl-calendar' className='brand-heading'>
						Season calendar
					</h2>
					<p>
						Closures and recital days at a glance. There are no lessons on the
						days marked closed, and no make-ups for a planned closure.
					</p>
				</div>
				<div className='fl-cal'>
					<SeasonMonthGrid showFlex={false} />
				</div>
			</section>

			{/* ---- 7. How make-ups work ---- */}
			<section className='fl' aria-labelledby='fl-makeups'>
				<div className='fl-sec__head'>
					<span className='eyebrow'>Cancellations</span>
					<h2 id='fl-makeups' className='brand-heading'>
						How make-ups work
					</h2>
				</div>

				<div className='cyv-card fl-card' style={{ '--accent': 'var(--grad-brand)' }}>
					<span className='accent-bar' />
					<ul className='fl-list'>
						<li>
							<FontAwesomeIcon icon={faCircleCheck} />
							<span>
								<strong>Cancel at least 24 hours ahead</strong> and a make-up
								credit is issued automatically. You do not need to ask us for
								it.
							</span>
						</li>
						<li>
							<FontAwesomeIcon icon={faCircleCheck} />
							<span>
								<strong>You book the make-up yourself</strong> from the open
								times in the portal, whenever one suits you. Your regular weekly
								time never moves.
							</span>
						</li>
						<li>
							<FontAwesomeIcon icon={faCircleCheck} />
							<span>
								<strong>Under 24 hours' notice</strong> is not eligible for a
								make-up. Make-ups are also subject to an open time being
								available, so earlier is better.
							</span>
						</li>
						<li>
							<FontAwesomeIcon icon={faCircleCheck} />
							<span>
								<strong>Credits are good through the end of the school year</strong>{' '}
								and do not carry into summer.
							</span>
						</li>
						<li>
							<FontAwesomeIcon icon={faCircleCheck} />
							<span>
								<strong>If we cancel, or weather closes us</strong>, the
								24-hour rule is waived and a make-up is guaranteed.
							</span>
						</li>
					</ul>
					<p className='fl-card__fine'>
						The full policy, including planned closures and withdrawal, is on{' '}
						<Link to='/policies#makeups'>our policies page</Link>.
					</p>
				</div>
			</section>

			{/* ---- 8. Questions ---- */}
			<section className='fl fl-cta' aria-labelledby='fl-questions'>
				<div className='fl-cta__in'>
					<span className='accent-bar' />
					<h2 id='fl-questions' className='brand-heading'>
						Still have a question?
					</h2>
					<p>
						Reply to any email from us, or call the studio. We would much rather
						answer it now than have you wonder about it all season.
					</p>
					<div className='fl-cta__btns'>
						<a className='btn-cyv-teal' href='tel:+19522830560'>
							<FontAwesomeIcon icon={faPhone} className='fl-i fl-i--before' />
							952-283-0560
						</a>
						<a className='btn-cyv-ghost' href='mailto:sing@coloryourvoice.com'>
							<FontAwesomeIcon icon={faEnvelope} className='fl-i fl-i--before' />
							sing@coloryourvoice.com
						</a>
					</div>
				</div>
			</section>
		</main>
	);
};

export default Fall;
